// SAD-459 (LIB-01) — TanStack Query bindings for the Library discovery home.
// Both scopes (Discover / My items) share ONE search RPC (zou's
// SearchLibraryItems) and differ only in the `scope` filter field
// ("" = discovery, "owner" = the viewer's own drafts/history) — see
// services/zou/pkg/connecthandlers/library.go.
import { Timestamp } from '@bufbuild/protobuf';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { projectDataClient } from '../../lib/connect/clients';
import { uploadAttachmentViaTesoro } from '../../lib/uploadAttachment';
import { pastPerformanceClient } from './ppClient';
export const LIBRARY_SEARCH_KEY = 'library-search';
function toTs(d) {
    return d ? Timestamp.fromDate(d) : undefined;
}
/** Searches Library items for one scope. Server drops anything the viewer may not see. */ export function useSearchLibraryItems(scope, filters, options = {}) {
    return useQuery({
        enabled: options.enabled ?? true,
        queryKey: [
            LIBRARY_SEARCH_KEY,
            scope,
            filters
        ],
        queryFn: async ()=>{
            const resp = await projectDataClient.searchLibraryItems({
                filter: {
                    scope,
                    query: filters.query ?? '',
                    itemType: filters.itemType ?? '',
                    customerAgencyName: filters.customerAgencyName ?? '',
                    providerCompanyProfileId: filters.providerCompanyProfileId ?? '',
                    scopeContext: filters.scopeContext ?? '',
                    capabilityContext: filters.capabilityContext ?? '',
                    status: filters.status ?? '',
                    updatedSince: toTs(filters.updatedSince)
                },
                offset: 0,
                limit: 50
            });
            return resp.items;
        }
    });
}
/** Creates a Library item. The server always stamps status=draft and owner=viewer. */ export function useCreateLibraryItem() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (args)=>{
            let references = [];
            if (args.file) {
                if (!args.uploader) throw new Error('Sign in again to attach a file');
                const uploaded = await uploadAttachmentViaTesoro(args.file, {
                    tenantId: args.uploader.tenantId,
                    ownerUserId: args.uploader.userId,
                    autoProcess: false
                });
                references = [
                    {
                        referenceKind: 'artifact',
                        artifactId: uploaded.artifactId,
                        label: args.file.name
                    }
                ];
            }
            const resp = await projectDataClient.createLibraryItem({
                references,
                title: args.title,
                summary: args.summary ?? '',
                itemType: args.itemType,
                customerAgencyName: args.customerAgencyName ?? '',
                providerCompanyProfileId: args.providerCompanyProfileId ?? '',
                scopeContext: args.scopeContext ?? '',
                capabilityContext: args.capabilityContext ?? '',
                preparedContent: args.preparedContent ?? '',
                preparedContentFormat: 'plain_text',
                owningScopeObjectType: args.owningScopeObjectType ?? '',
                owningScopeObjectId: args.owningScopeObjectId ?? ''
            });
            if (!resp.item) throw new Error('CreateLibraryItem returned no item');
            return resp.item;
        },
        onSuccess: ()=>{
            queryClient.invalidateQueries({
                queryKey: [
                    LIBRARY_SEARCH_KEY
                ]
            });
        },
        onError: (err)=>{
            toast.error(err instanceof Error ? err.message : 'Failed to create Library item');
        }
    });
}
export const LIBRARY_ITEM_FILES_KEY = 'library-item-files';
/** The files an item carries (a template's .docx, an asset's image). */ export function useLibraryItemFiles(itemId) {
    return useQuery({
        queryKey: [
            LIBRARY_ITEM_FILES_KEY,
            itemId
        ],
        queryFn: async ()=>{
            const resp = await projectDataClient.getLibraryItem({
                id: itemId
            });
            return (resp.references ?? []).filter((r)=>r.artifactId).map((r)=>({
                    artifactId: r.artifactId,
                    label: r.label
                }));
        },
        staleTime: 60000
    });
}
export const PP_REFERENCES_KEY = 'library-pp-references';
/** Past Performance details keyed by library item id, for merging onto item cards. */ export function usePastPerformanceDetails() {
    return useQuery({
        queryKey: [
            PP_REFERENCES_KEY
        ],
        queryFn: async ()=>{
            const resp = await pastPerformanceClient.listPastPerformanceReferences({
                offset: 0,
                limit: 200
            });
            const byItem = new Map();
            for (const r of resp.references){
                if (r.item && r.details) byItem.set(r.item.id, r.details);
            }
            return byItem;
        }
    });
}
/** Creates an independent Past Performance Reference (no Program link). */ export function useCreatePastPerformanceReference() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (args)=>{
            const resp = await pastPerformanceClient.createPastPerformanceReference({
                title: args.title,
                summary: args.summary ?? '',
                customerAgencyName: args.customerAgencyName ?? '',
                providerCompanyProfileId: args.providerCompanyProfileId ?? '',
                scopeContext: args.scopeContext ?? '',
                capabilityContext: args.capabilityContext ?? '',
                preparedContent: args.preparedContent ?? '',
                preparedContentFormat: 'plain_text',
                details: {
                    referenceOrigin: args.details.origin,
                    providerRole: args.details.providerRole.trim(),
                    limitations: args.details.limitations.trim(),
                    basisKind: args.details.basisKind,
                    outcome: args.details.outcome.trim(),
                    referenceContact: args.details.referenceContact.trim()
                }
            });
            if (!resp.reference?.item) throw new Error('CreatePastPerformanceReference returned no item');
            return resp.reference;
        },
        onSuccess: ()=>{
            queryClient.invalidateQueries({
                queryKey: [
                    LIBRARY_SEARCH_KEY
                ]
            });
            queryClient.invalidateQueries({
                queryKey: [
                    PP_REFERENCES_KEY
                ]
            });
        },
        onError: (err)=>{
            toast.error(err instanceof Error ? err.message : 'Failed to create Past Performance Reference');
        }
    });
}
